package fyi.moko.hotpl.domain.model

import kotlinx.serialization.Serializable

/**
 * A single visit (or revisit) to a place. [visitedAt] is epoch milliseconds.
 * [emotion] is the persisted [Emotion.id] of a single-selected, optional emotion tag.
 *
 * Since Stage 2 (visits subcollection sync), a visit carries the same ownership/sync
 * metadata as a [Place]: [id] is a client-generated UUID (also the Firestore document
 * id under `users/{uid}/places/{placeId}/visits/{visitId}`), [ownerId] ties it to the
 * account, [syncState]/[version]/[isDeleted] drive the sync engine, and
 * [createdAt]/[updatedAt] are the edit timestamps.
 *
 * Visit modification policy (§14.1 1-5): append-only + immediate edit allowed. A
 * visit is created with [note]/[emotion], may be edited in the same session, and
 * after that is effectively immutable (the sync engine still respects version +
 * updatedAt for LWW on the off chance a late edit races a push).
 */
@Serializable
data class Visit(
    val id: String = "",
    val placeId: String,
    val note: String? = null,
    val visitedAt: Long,
    val emotion: String? = null,
    /**
     * File name (not a full path) of a photo taken when logging this visit, stored in the
     * app's local image dir via [fyi.moko.hotpl.data.image.PlaceImageStore], or null.
     *
     * ── 파일명은 동기화되지만 바이트는 기기에 남는다 ──────────────────────────────
     *  · [fyi.moko.hotpl.domain.model.Place.imageFileName]과 같은 규칙이다. 다른
     *    기기에서 이 방문을 받으면 이름은 있고 파일은 없으므로, 로드가 null을
     *    돌려주고 UI는 사진 없는 방문으로 그린다 — 깨진 이미지가 아니라 없는 것.
     *  · 백업에는 넣지 않는다. 복원한 기기에 파일이 없는 건 마찬가지인데,
     *    이름만 살려두면 "있었는데 사라진" 것처럼 보인다.
     */
    val imageFileName: String? = null,
    /** 이 방문에 붙은 사진들. 그날 먹은 것과 함께 간 사람. [imageFileName]은 그 중 대표. */
    val photos: List<PhotoRef> = emptyList(),
    val ownerId: String = "",
    val syncState: SyncState = SyncState.SYNCED,
    val version: Long = 1L,
    val isDeleted: Boolean = false,
    val createdAt: Long = 0L,
    val updatedAt: Long = 0L,
)
